// Google Analytics 4 via gtag.js. The GA4 measurement ID and the Google Ads
// conversion ID are publishable by design (they appear in the source of every
// GA-/Ads-enabled site), so it is fine to keep them here as literals. These are
// the project's own properties.
import { CONSENT_KEY, CONSENT_RECORD_KEY, leesConsent, zetStandaardConsent } from "@/lib/consent";

const MEASUREMENT_ID = "G-K1X7007BEX";
const ADS_ID = "AW-18487950558";
// Existing Google Ads website action: "Leadformulier indienen (1)".
const LEAD_CONVERSION = "AW-18487950558/HW5lCMSQqo4dEN753u9E";
export const GTM_ID = "GTM-N6HV2LRT";

let initialized = false;

/** Load gtag.js once and send the initial page view. Safe to call on every render. */
export function initAnalytics() {
  if (typeof window === "undefined" || !MEASUREMENT_ID) return;
  if (!initialized) {
    // Register once; existing choices are applied before loading any provider tags.
    window.addEventListener("eo-consent-changed", loadConsentedAnalytics);
    window.addEventListener("storage", (event) => {
      if (event.key === CONSENT_KEY || event.key === CONSENT_RECORD_KEY) {
        if (leesConsent() === "geaccepteerd") loadConsentedAnalytics();
        else window.location.reload();
      }
    });
    initialized = true;
  }
  if (leesConsent() === "geaccepteerd") loadConsentedAnalytics();
}

let tagsLoaded = false;

function loadConsentedAnalytics() {
  if (typeof window === "undefined") return;
  if (tagsLoaded && leesConsent() !== "geaccepteerd") {
    window.location.reload();
    return;
  }
  if (tagsLoaded || leesConsent() !== "geaccepteerd") return;
  tagsLoaded = true;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function (..._args: unknown[]) {
    // Google verwacht het echte arguments object, geen array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Consent Mode v2: standaard geweigerd tot de bezoeker kiest.
  zetStandaardConsent();
  window.gtag("consent", "update", {
    ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });
  // Google Ads conversie-id: dezelfde gtag.js-lading bedient beide properties.
  if (ADS_ID) window.gtag("config", ADS_ID);

  // Google Tag Manager-container: zelfde dataLayer, zelfde Consent Mode.
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
  const gtmScript = document.createElement("script");
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(gtmScript);

  trackPageView(window.location.pathname);
}

function trackPageView(path: string) {
  if (typeof window === "undefined" || !window.gtag || !MEASUREMENT_ID) return;
  window.gtag("event", "page_view", { page_path: path });
}

/** Fire a page view for the given path. Called by the app on route changes. */
export function trackPageViewExternal(path: string) {
  trackPageView(path);
}

/** A single consented, successful lead becomes a GTM custom event (no customer data). */
export function trackSubmittedLead(leadId: string) {
  if (typeof window === "undefined" || leesConsent() !== "geaccepteerd") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "eo_lead_submitted", transaction_id: leadId });
  // The existing GTM container does not fire this conversion. Send it once
  // through the already-loaded Google tag, using the persisted lead ID to dedupe.
  window.gtag?.("event", "conversion", {
    send_to: LEAD_CONVERSION,
    transaction_id: leadId,
  });
}
